/* Foto-album voor ouder-portaal — chronologisch overzicht met filter per week / maand / alles.
 * Groepeert foto's per dag, opent in een lightbox bij tap. */
import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Baby, ChevronLeft, Camera, X, Heart, ThumbsUp } from "lucide-react";
import { toast } from "sonner";

const photoUrl = (url) => (url?.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url);

function monthRange(iso) {
  const d = new Date(iso);
  const from = new Date(d.getFullYear(), d.getMonth(), 1);
  const to = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

function weekRange(iso) {
  const d = new Date(iso);
  const dow = (d.getDay() + 6) % 7; // ma=0
  const from = new Date(d); from.setDate(d.getDate() - dow);
  const to = new Date(from); to.setDate(from.getDate() + 6);
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

export default function PortaalAlbumPage() {
  const { user, status } = useAuth();
  const navigate = useNavigate();
  const [kinderen, setKinderen] = useState([]);
  const [selectedKid, setSelectedKid] = useState(null);
  const [mode, setMode] = useState("month"); // "week" | "month" | "all"
  const [cursor, setCursor] = useState(() => new Date().toISOString().slice(0, 10));
  const [photos, setPhotos] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [loading, setLoading] = useState(false);

  const range = useMemo(() => {
    if (mode === "week") return weekRange(cursor);
    if (mode === "month") return monthRange(cursor);
    return { from: null, to: null };
  }, [mode, cursor]);

  useEffect(() => {
    if (status !== "authed" || user?.role !== "ouder") return;
    api.get("/portaal/overview")
      .then(r => {
        setKinderen(r.data.kinderen || []);
        if ((r.data.kinderen || []).length) setSelectedKid(r.data.kinderen[0]);
      })
      .catch(e => toast.error(formatApiError(e.response?.data?.detail)));
  }, [status, user]);

  useEffect(() => {
    if (!selectedKid) return;
    setLoading(true);
    const params = { limit: 500 };
    if (range.from) params.date_from = range.from;
    if (range.to) params.date_to = range.to;
    api.get(`/portaal/kind/${selectedKid.id}/photos`, { params })
      .then(r => setPhotos(r.data || []))
      .catch(() => setPhotos([]))
      .finally(() => setLoading(false));
  }, [selectedKid, range.from, range.to]);

  // Group photos per day
  const grouped = useMemo(() => {
    const g = {};
    photos.forEach(p => {
      const day = (p.created_at || "").slice(0, 10) || "onbekend";
      (g[day] ||= []).push(p);
    });
    return Object.entries(g).sort((a, b) => b[0].localeCompare(a[0]));
  }, [photos]);

  const shift = (delta) => {
    const d = new Date(cursor);
    if (mode === "week") d.setDate(d.getDate() + delta * 7);
    else if (mode === "month") d.setMonth(d.getMonth() + delta);
    else return;
    setCursor(d.toISOString().slice(0, 10));
  };

  const labelForRange = () => {
    if (mode === "all") return "Alles";
    const d = new Date(cursor);
    if (mode === "week") {
      const from = new Date(range.from);
      const to = new Date(range.to);
      return `${from.toLocaleDateString("nl-NL", { day: "numeric", month: "short" })} – ${to.toLocaleDateString("nl-NL", { day: "numeric", month: "short" })}`;
    }
    return d.toLocaleDateString("nl-NL", { month: "long", year: "numeric" });
  };

  if (status === "loading") return <div className="min-h-screen flex items-center justify-center bg-[color:var(--bone)]">Laden…</div>;
  if (status === "anon") return <Navigate to="/login" replace />;
  if (user?.role !== "ouder") return <Navigate to="/app" replace />;

  return (
    <div className="min-h-screen bg-[color:var(--bone)] subtle-grain" data-testid="portaal-album-page">
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/portaal")} data-testid="album-back-btn">
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ background: "var(--sage)" }}>
            <Camera className="h-4 w-4 text-white" />
          </div>
          <div className="min-w-0">
            <div className="font-display text-sm leading-tight truncate">Foto's</div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Album</div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-4 pb-24 space-y-4">
        {/* Kind-selector als >1 kind */}
        {kinderen.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-1" data-testid="album-kind-picker">
            {kinderen.map(k => (
              <button
                key={k.id}
                onClick={() => setSelectedKid(k)}
                data-testid={`album-kid-${k.id}`}
                className={`shrink-0 px-3 py-1.5 rounded-full border text-sm ${selectedKid?.id === k.id ? "bg-[color:var(--sage)] text-white border-[color:var(--sage)]" : "border-neutral-200 bg-white"}`}
              >
                {k.voornaam}
              </button>
            ))}
          </div>
        )}

        {/* Filter-strip */}
        <Card className="p-3 bg-white shadow-none" data-testid="album-filter-card">
          <div className="flex items-center gap-2 mb-2">
            {["week", "month", "all"].map(m => (
              <button
                key={m}
                onClick={() => setMode(m)}
                data-testid={`album-mode-${m}`}
                className={`px-3 py-1 rounded-md text-xs font-medium ${mode === m ? "bg-[color:var(--sage)] text-white" : "bg-neutral-100 text-neutral-600"}`}
              >
                {m === "week" ? "Week" : m === "month" ? "Maand" : "Alles"}
              </button>
            ))}
          </div>
          {mode !== "all" && (
            <div className="flex items-center justify-between text-sm">
              <Button variant="ghost" size="sm" onClick={() => shift(-1)} data-testid="album-prev">← Vorige</Button>
              <div className="font-medium text-neutral-800 capitalize" data-testid="album-range-label">{labelForRange()}</div>
              <Button variant="ghost" size="sm" onClick={() => shift(1)} data-testid="album-next">Volgende →</Button>
            </div>
          )}
        </Card>

        {selectedKid && !selectedKid.toestemming_foto && (
          <Card className="p-4 bg-amber-50 border border-amber-200 text-sm text-amber-800" data-testid="album-no-consent">
            Foto's zijn uitgeschakeld voor {selectedKid.voornaam}. Zet toestemming aan in de instellingen om ze te zien.
          </Card>
        )}

        {loading && <div className="text-sm text-neutral-500 text-center py-6" data-testid="album-loading">Laden…</div>}

        {!loading && grouped.length === 0 && (
          <Card className="p-6 bg-white text-center" data-testid="album-empty">
            <Baby className="h-8 w-8 text-neutral-300 mx-auto mb-2" />
            <div className="text-sm text-neutral-500">
              Geen foto's gevonden voor {selectedKid?.voornaam || "je kind"} in deze periode.
            </div>
          </Card>
        )}

        {grouped.map(([day, ps]) => (
          <div key={day} data-testid={`album-day-${day}`}>
            <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold mb-1.5 px-1">
              {new Date(day).toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long" })}
              <span className="ml-2 normal-case tracking-normal text-neutral-400 font-normal">{ps.length} {ps.length === 1 ? "foto" : "foto's"}</span>
            </div>
            <div className="grid grid-cols-3 gap-1">
              {ps.map(p => {
                const rCount = (p.reactions || []).length;
                return (
                <button
                  key={p.id}
                  onClick={() => setLightbox(p)}
                  className="relative aspect-square bg-neutral-100 rounded overflow-hidden active:opacity-70"
                  data-testid={`album-photo-${p.id}`}
                >
                  <img
                    src={photoUrl(p.url)}
                    alt={p.caption || "foto"}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  {rCount > 0 && (
                    <div
                      className="absolute bottom-1 right-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded-full flex items-center gap-0.5"
                      data-testid={`album-photo-reactions-${p.id}`}
                    >
                      <Heart className="h-2.5 w-2.5 fill-current" /> {rCount}
                    </div>
                  )}
                </button>
                );
              })}
            </div>
          </div>
        ))}
      </main>

      {lightbox && (
        <LightboxWithReactions
          photo={lightbox}
          onClose={() => setLightbox(null)}
          onUpdate={(updated) => {
            setLightbox(updated);
            setPhotos(ps => ps.map(p => p.id === updated.id ? updated : p));
          }}
        />
      )}
    </div>
  );
}

function LightboxWithReactions({ photo, onClose, onUpdate }) {
  const { user } = useAuth();
  const myHeart = (photo.reactions || []).find(r => r.parent_id === user?.parent_id && r.emoji === "heart");
  const [message, setMessage] = useState(myHeart?.message || "");
  const [busy, setBusy] = useState(false);

  const sendHeart = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/photos/${photo.id}/react`, { emoji: "heart", message: message.trim() });
      onUpdate({ ...photo, reactions: r.data.reactions });
      if (message.trim()) toast.success("Hartje + berichtje verstuurd");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reactie mislukt");
    } finally { setBusy(false); }
  };

  const toggleThumbs = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/photos/${photo.id}/react`, { emoji: "thumbs_up" });
      onUpdate({ ...photo, reactions: r.data.reactions });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reactie mislukt");
    } finally { setBusy(false); }
  };

  const heartCount = (photo.reactions || []).filter(r => r.emoji === "heart").length;
  const thumbsCount = (photo.reactions || []).filter(r => r.emoji === "thumbs_up").length;
  const thumbsMine = (photo.reactions || []).some(r => r.parent_id === user?.parent_id && r.emoji === "thumbs_up");

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 bg-black/90 flex flex-col items-center justify-center p-3"
      data-testid="album-lightbox"
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-4 right-4 text-white/80 hover:text-white z-10"
        data-testid="album-lightbox-close"
      >
        <X className="h-7 w-7" />
      </button>

      <div className="flex-1 flex items-center justify-center w-full" onClick={(e) => e.stopPropagation()}>
        <img
          src={photo.url?.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${photo.url}` : photo.url}
          alt={photo.caption || "foto"}
          className="max-w-full max-h-[60vh] rounded"
        />
      </div>

      {photo.caption && (
        <div className="text-center text-white text-sm px-4 py-2" onClick={(e) => e.stopPropagation()}>
          {photo.caption}
        </div>
      )}

      {/* Reactie-strip */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-black/40 backdrop-blur-md rounded-lg p-3 space-y-2"
        data-testid="album-reaction-strip"
      >
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={message}
            onChange={e => setMessage(e.target.value.slice(0, 240))}
            placeholder="Privé-bericht bij je hartje (optioneel)"
            data-testid="album-reaction-message"
            className="flex-1 bg-white/10 text-white placeholder:text-white/50 rounded-full px-4 py-2 text-sm border border-white/20 focus:outline-none focus:border-white/50"
            maxLength={240}
          />
          <button
            onClick={sendHeart}
            disabled={busy}
            data-testid="album-react-heart"
            className={`shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full font-medium transition-all ${
              myHeart ? "bg-red-500 text-white" : "bg-white/20 text-white hover:bg-white/30"
            }`}
          >
            <Heart className={`h-5 w-5 ${myHeart ? "fill-current" : ""}`} />
            {heartCount > 0 && <span className="text-sm">{heartCount}</span>}
          </button>
          <button
            onClick={toggleThumbs}
            disabled={busy}
            data-testid="album-react-thumbs_up"
            className={`shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full font-medium transition-all ${
              thumbsMine ? "bg-blue-500 text-white" : "bg-white/20 text-white hover:bg-white/30"
            }`}
          >
            <ThumbsUp className={`h-5 w-5 ${thumbsMine ? "fill-current" : ""}`} />
            {thumbsCount > 0 && <span className="text-sm">{thumbsCount}</span>}
          </button>
        </div>
        {myHeart?.message && (
          <div className="text-[11px] text-white/70 italic px-2" data-testid="album-my-message">
            Jouw berichtje: "{myHeart.message}"
          </div>
        )}
        <div className="text-[10px] text-white/50 px-2">
          Alleen de leidsters van {photo.author_name || "de opvang"} zien dit bericht.
        </div>
      </div>
    </div>
  );
}

